import { type ReactNode, useMemo, useRef } from 'react'

import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'
import { get, isEmpty, isString } from 'lodash'
import { useFormContext, type Validate } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { ScreenReaderBreak, useMergedRefs, useUnique } from '@subaio/components'

import { ColumnLabel, InputError, Label, Placeholder } from '../Shared'
import { InputComponent } from './InputComponent'
import { InputWrapper } from './InputWrapper'
import { Textarea } from './Textarea'

type Props = {
	label: ReactNode
	name: string

	autoComplete?: string
	isDisabled?: boolean
	isReadOnly?: boolean
	isRequired?: boolean
	maxLength?: number
	maxRows?: number
	minLength?: number
	minRows?: number
	placeholder?: string
	shouldAutofocus?: boolean
	showOptionalHint?: boolean
	validate?: Validate<any, any>
	type?: 'email' | 'hidden' | 'number' | 'password' | 'tel' | 'text' | 'username' | 'multiline'
}

export const Input = ({
	isRequired = false,
	type = 'text',
	isDisabled = false,
	minLength,
	maxLength,
	name,
	autoComplete = 'off',
	isReadOnly = false,
	label,
	placeholder,
	shouldAutofocus,
	validate,
	minRows = 3,
	maxRows = 8,
}: Props) => {
	const id = useUnique(name)
	const {
		register,
		formState: { errors },
		watch,
	} = useFormContext()
	const value = watch(name, undefined) as Date | number | string | undefined
	const { isFocused, focusProps } = useFocusRing({ isTextInput: true, autoFocus: shouldAutofocus })
	const { hoverProps, isHovered } = useHover({})
	const inputRef = useRef<HTMLInputElement>(null)
	const { formatMessage } = useIntl()

	const inputMode = useMemo(() => {
		switch (type) {
			case 'email':
				return 'email'
			case 'hidden':
				return 'none'
			case 'number':
				return 'numeric'
			case 'tel':
				return 'tel'
			default:
				return 'text'
		}
	}, [type])
	const inputType = useMemo(() => {
		switch (type) {
			case 'number':
				return 'text'
			default:
				return type
		}
	}, [type])

	const inputProps = register(name, {
		required: isRequired
			? formatMessage({ id: 'formFields.input.isRequired', defaultMessage: 'This field is required' })
			: false,
		disabled: isDisabled,
		validate: {
			customValidation: validate ?? (() => true),
		},
		setValueAs: (value: unknown) => {
			if (type === 'number') {
				if (isEmpty(value) || Number.isNaN(value)) return undefined
				return isString(value) && !isEmpty(value.trim()) ? Number(value) : undefined
			}
			return isString(value) && isEmpty(value) ? undefined : value
		},
	})
	const sharedRef = useMergedRefs<HTMLInputElement | HTMLTextAreaElement>(inputProps.ref, inputRef)

	const error = get(errors, name)
	const hasError = Boolean(error)
	const hasContent = Boolean(value)
	const defaultProps = {
		...focusProps,
		...hoverProps,
		// Avoid typing issue with react-autosize-textarea
		style: undefined,
		'aria-disabled': isReadOnly || isDisabled,
		'aria-required': isRequired,
		'aria-invalid': hasError,
		'aria-labelledby': `${id}-label`,
		'aria-describedby': `${id}-error`,
		autoFocus: shouldAutofocus,
		type: inputType,
		// Needs to be passed as both `readOnly` (for HTML) and `isReadOnly` (for styling)
		readOnly: isReadOnly,
		ref: sharedRef,
		autoComplete,
		hasError,
		id,
		inputMode,
		isDisabled,
		isFocused,
		isHovered,
		isReadOnly,
		maxLength,
		minLength,
	} as const

	return (
		<ColumnLabel>
			{label && (
				<Label
					id={`${id}-label`}
					htmlFor={id}
					hasError={hasError}
					hasContent={hasContent}
					isFocused={isFocused && !isReadOnly && !isDisabled}
					isDisabled={isDisabled}
					aria-hidden
				>
					{label}
					<ScreenReaderBreak />
				</Label>
			)}
			{placeholder && (
				<Placeholder hasError={hasError} isVisible={!hasContent && !isFocused}>
					{placeholder}
				</Placeholder>
			)}
			<InputWrapper>
				{type === 'multiline' ? (
					<Textarea {...inputProps} {...defaultProps} minRows={minRows} maxRows={maxRows} />
				) : (
					<InputComponent {...inputProps} {...defaultProps} />
				)}
			</InputWrapper>
			<InputError id={`${id}-error`} hasError={hasError} errorMessage={error?.message as string | undefined} />
		</ColumnLabel>
	)
}
